import { useState } from "react";

const useSplitBill = ({ pricePrecision }) => {
  const [itemSplitMap, setItemSplitMap] = useState({});
  const [splitOnItem, setSplitOnItem] = useState(false);
  const [groupedItems, setGroupedItems] = useState([]);
  const [categorySplitSelection, setCategorySplitSelection] = useState({});
  const [openCategory, setOpenCategory] = useState(null);

  const toggleCategory = (index) => {
    setOpenCategory(openCategory === index ? null : index);
  };

  const splitWithPrecision = (amount, splits, precision) => {
    const factor = Math.pow(10, precision);
    const base = Math.floor((amount / splits) * factor) / factor;
    const distributed = base * splits;
    let remainder = Math.round((amount - distributed) * factor);
    const nextDigit = Math.floor(((amount / splits) * factor * 10) % 10);
    const results = Array(splits).fill(base);
    if (remainder > 0) {
      if (nextDigit >= 5) {
        for (let i = 0; i < remainder; i++) {
          results[i] += 1 / factor;
        }
      } else {
        results[results.length - 1] += remainder / factor;
      }
    }
    return results.map((v) => +v.toFixed(precision));
  };

  const buildSplitCarts = (cart, numSplits, map) => {
    const splits = Array.from({ length: numSplits }, (_, i) => ({
      ...cart,
      splitNo: i + 1,
      items: [],
      total: 0,
      tax: 0,
      discount: 0,
      deliveryCharge: 0,
      packingCharge: 0,
      tip: 0,
      serviceCharge: 0,
    }));

    cart.items.forEach((item) => {
      const flags = map[item.lineId] || Array(numSplits).fill(true);
      const activeIdx = flags.map((on, idx) => (on ? idx : null)).filter((v) => v !== null);
      if (activeIdx.length === 0) return;
      const share = activeIdx.length;
      const netSplit = Number(item.nettotal) / share;
      const taxSplit = Number(item.taxAmount || 0) / share;
      activeIdx.forEach((i) => {
        splits[i].items.push({ ...item, nettotal: netSplit, tax: taxSplit });
      });
    });

    splits.forEach((s) => {
      s.total = +s.items.reduce((sum, it) => sum + it.nettotal, 0).toFixed(pricePrecision);
      s.tax = +s.items.reduce((sum, it) => sum + it.tax, 0).toFixed(pricePrecision);
    });

    const calcTotal = splits.reduce((sum, s) => sum + s.total, 0);
    const diff = +(cart.total - calcTotal).toFixed(pricePrecision);
    if (diff !== 0) {
      const idx = splits.findIndex((s) => s.total > 0);
      if (idx !== -1) splits[idx].total = +(splits[idx].total + diff).toFixed(pricePrecision);
    }

    const calcTax = splits.reduce((sum, s) => sum + s.tax, 0);
    const taxDiff = +(cart.tax - calcTax).toFixed(pricePrecision);
    if (taxDiff !== 0) {
      const idx = splits.findIndex((s) => s.tax > 0);
      if (idx !== -1) splits[idx].tax = +(splits[idx].tax + taxDiff).toFixed(pricePrecision);
    }

    return splits;
  };

  const handleToggleItemSplit = (item, splitIndex, cart, numSplits, setSplitCarts) => {
    setSplitOnItem(true);
    setItemSplitMap((prev) => {
      const current = prev[item.lineId] || Array(numSplits).fill(true);
      const next = [...current];
      const numOn = current.filter(Boolean).length;
      if (current[splitIndex] && numOn === 1) return prev;
      next[splitIndex] = !current[splitIndex];
      const newMap = { ...prev, [item.lineId]: next };
      const newSplitCarts = buildSplitCarts(cart, numSplits, newMap);
      setSplitCarts(newSplitCarts);
      setSplitOnItem(false);
      return newMap;
    });
  };

  const assignWholeCategory = (group, splitIndexToRemove, cart, numSplits, setSplitCarts, undo = false) => {
    setItemSplitMap((prev) => {
      let updated = { ...prev };
      group.items.forEach((item) => {
        if (undo) {
          updated[item.lineId] = Array(numSplits).fill(true);
          return;
        }
        const current = prev[item.lineId] || Array(numSplits).fill(true);
        const numOn = current.filter(Boolean).length;
        if (current[splitIndexToRemove] && numOn === 1) {
          updated[item.lineId] = current;
          return;
        }
        const arr = Array(numSplits).fill(true);
        arr[splitIndexToRemove] = false;
        updated[item.lineId] = arr;
      });
      const newSplitCarts = buildSplitCarts(cart, numSplits, updated);
      setSplitCarts(newSplitCarts);
      return updated;
    });
  };

  return {
    itemSplitMap,
    setItemSplitMap,
    splitOnItem,
    setSplitOnItem,
    groupedItems,
    setGroupedItems,
    categorySplitSelection,
    setCategorySplitSelection,
    openCategory,
    setOpenCategory,
    toggleCategory,
    splitWithPrecision,
    buildSplitCarts,
    handleToggleItemSplit,
    assignWholeCategory,
  };
};

export default useSplitBill;
